import { ConfigProvider, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardOutlined, DatabaseOutlined } from '@ant-design/icons';

import { dmsAntdTheme } from '../../config/antdTheme';
import { RTU_ICONS } from '../../constants/rtuIcons';
import AdminPingSettingsPage from '../AdminPingSettingsPage/AdminPingSettingsPage';
import DashboardDefaultsPanel from './DashboardDefaultsPanel';
import RetentionPoliciesPanel from './RetentionPoliciesPanel';
import './AdminSettingsPage.css';

type SettingsSection = 'reachability' | 'system-defaults' | 'retention';
type SettingsPage = 'ping-templates' | 'storage-attention' | 'policies';
type SettingsKey = `${SettingsSection}/${SettingsPage}`;

const DEFAULT_SETTINGS_PATH = '/admin/settings/reachability/ping-templates';
const STORAGE_ATTENTION_PATH = '/admin/settings/system-defaults/storage-attention';

const VALID_SETTINGS_KEYS = new Set<SettingsKey>([
  'reachability/ping-templates',
  'system-defaults/storage-attention',
  'retention/policies',
]);

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { section, page } = useParams<{ section?: string; page?: string }>();
  const [searchParams] = useSearchParams();

  const menuItems = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'reachability-group',
        label: 'Reachability',
        type: 'group',
        children: [
          {
            key: 'reachability/ping-templates',
            label: 'Ping templates',
            icon: (
              <span
                aria-hidden="true"
                className="admin-settings-page__menu-icon"
                style={{
                  WebkitMask: `url(${RTU_ICONS.PING}) center / contain no-repeat`,
                  mask: `url(${RTU_ICONS.PING}) center / contain no-repeat`,
                }}
              />
            ),
          },
        ],
      },
      {
        key: 'dashboard-group',
        label: 'System Defaults',
        type: 'group',
        children: [
          {
            key: 'system-defaults/storage-attention',
            label: 'Storage Attention',
            icon: <DashboardOutlined />,
          },
        ],
      },
      {
        key: 'retention-group',
        label: 'Data Lifecycle',
        type: 'group',
        children: [
          {
            key: 'retention/policies',
            label: 'Retention policies',
            icon: <DatabaseOutlined />,
          },
        ],
      },
    ],
    []
  );

  if (!section) {
    return <Navigate to={DEFAULT_SETTINGS_PATH} replace />;
  }

  if (section === 'dashboard-defaults') {
    return <Navigate to={`${STORAGE_ATTENTION_PATH}${location.search}`} replace />;
  }

  if (section === 'reachability' && !page) {
    return <Navigate to={DEFAULT_SETTINGS_PATH} replace />;
  }

  if (section === 'system-defaults' && !page) {
    return <Navigate to={`${STORAGE_ATTENTION_PATH}${location.search}`} replace />;
  }

  if (section === 'retention' && !page) {
    return <Navigate to="/admin/settings/retention/policies" replace />;
  }

  const selectedKey = `${section}/${page}` as SettingsKey;
  if (!VALID_SETTINGS_KEYS.has(selectedKey)) {
    return <Navigate to={DEFAULT_SETTINGS_PATH} replace />;
  }

  const isReachabilityTemplates = selectedKey === 'reachability/ping-templates';
  const isRetentionPolicies = selectedKey === 'retention/policies';

  return (
    <ConfigProvider theme={dmsAntdTheme}>
      <div className="admin-settings-page">
        {/* The breadcrumb already shows "Master Settings" as the page title
            (ACT-UI-011); this hidden h1 keeps a real heading in the
            accessibility tree without repeating it on screen. */}
        <h1 className="dms-visually-hidden">Master Settings</h1>
        <aside className="admin-settings-page__nav" aria-label="Master Settings sections">
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            items={menuItems}
            onClick={({ key }) => navigate(`/admin/settings/${key}`)}
          />
        </aside>
        <main className="admin-settings-page__content">
          {isReachabilityTemplates ? (
            <AdminPingSettingsPage />
          ) : isRetentionPolicies ? (
            <RetentionPoliciesPanel />
          ) : (
            <DashboardDefaultsPanel highlightKey={searchParams.get('highlight')} />
          )}
        </main>
      </div>
    </ConfigProvider>
  );
};

export default AdminSettingsPage;
